import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/use-toast";
import { Loader2, Mail, Phone, Building, User, Package } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface DemoRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planName?: string;
}

export function DemoRequestDialog({ open, onOpenChange, planName }: DemoRequestDialogProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    employees: "",
    message: "",
    selectedPlan: planName || "",
    modules: {
      whistleblow: false,
      facilityManagement: false,
      riskAssessment: false,
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.selectedPlan) {
      toast({
        title: "Plan Required",
        description: "Please select a pricing plan.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Call Supabase Edge Function to send email via Resend
      const { data, error } = await supabase.functions.invoke('send-demo-request', {
        body: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          employees: formData.employees,
          selectedPlan: formData.selectedPlan,
          modules: formData.modules,
          message: formData.message,
        },
      });

      if (error) {
        console.error('Error sending demo request:', error);
        throw error;
      }

      console.log('Demo request sent successfully:', data);

      toast({
        title: "Demo Request Sent!",
        description: "We'll contact you shortly to schedule your free demo.",
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        employees: "",
        message: "",
        selectedPlan: "",
        modules: {
          whistleblow: false,
          facilityManagement: false,
          riskAssessment: false,
        },
      });

      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send demo request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">Request Free Demo</DialogTitle>
          <DialogDescription>
            {planName ? `Interested in the ${planName} plan? ` : ""}
            Fill out the form below and we'll contact you to schedule a personalized demo.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Full Name *
            </Label>
            <Input
              id="name"
              placeholder="John Doe"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email Address *
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="john@company.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Phone Number *
            </Label>
            <Input
              id="phone"
              type="tel"
              placeholder="+30 123 456 7890"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="company" className="flex items-center gap-2">
              <Building className="h-4 w-4" />
              Company Name *
            </Label>
            <Input
              id="company"
              placeholder="Your Company Ltd."
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="employees">Number of Employees</Label>
            <Input
              id="employees"
              type="number"
              placeholder="e.g., 25"
              value={formData.employees}
              onChange={(e) => setFormData({ ...formData, employees: e.target.value })}
            />
          </div>

          {/* Pricing Plan Selection */}
          <div className="space-y-3 pt-2 border-t border-gray-200">
            <Label className="flex items-center gap-2 text-base font-semibold">
              <Package className="h-5 w-5" />
              Select Pricing Plan *
            </Label>
            <div className="space-y-2">
              {["Basic", "Advanced", "Enterprise"].map((plan) => (
                <div key={plan} className="flex items-center space-x-2">
                  <Checkbox
                    id={`plan-${plan}`}
                    checked={formData.selectedPlan === plan}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setFormData({ ...formData, selectedPlan: plan });
                      }
                    }}
                  />
                  <label
                    htmlFor={`plan-${plan}`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {plan} Plan
                    {plan === "Basic" && " - €50 for first 10 users + €6/user"}
                    {plan === "Advanced" && " - €99 for first 20 users + €5/user"}
                    {plan === "Enterprise" && " - €250 for first 50 users + €5/user"}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Optional Modules */}
          <div className="space-y-3 pt-2 border-t border-gray-200">
            <Label className="flex items-center gap-2 text-base font-semibold">
              <Package className="h-5 w-5" />
              Optional Add-on Modules
            </Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="module-whistleblow"
                  checked={formData.modules.whistleblow}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      modules: { ...formData.modules, whistleblow: !!checked },
                    })
                  }
                />
                <label
                  htmlFor="module-whistleblow"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Whistleblow Module - €0.50/employee/month
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="module-facility"
                  checked={formData.modules.facilityManagement}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      modules: { ...formData.modules, facilityManagement: !!checked },
                    })
                  }
                />
                <label
                  htmlFor="module-facility"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Facility Management - €2/employee/month
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="module-risk"
                  checked={formData.modules.riskAssessment}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      modules: { ...formData.modules, riskAssessment: !!checked },
                    })
                  }
                />
                <label
                  htmlFor="module-risk"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Risk Assessment Module - €100/month (flat rate)
                </label>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Additional Message</Label>
            <Textarea
              id="message"
              placeholder="Tell us about your needs..."
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={4}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Request Demo"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
